import React, { useState, useEffect, useCallback } from 'react';
import { Pill, Plus, Search, X, Package, AlertTriangle, Clock, CheckCircle, AlertCircle, Edit2, RefreshCw, Eye, Trash2, TrendingDown, ShoppingCart, History, PackagePlus, ArrowRight } from 'lucide-react';
import { medicineAPI, vendorAPI, plannerAPI, stockReceiptAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import DatePicker from './DatePicker';
import { toTitleCase } from '../utils/text';
import { formatClinicDate, toClinicDate } from '../utils/clinicTime';
import RowActionsMenu from './RowActionsMenu';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import Select from './Select';
import useDialog from '../hooks/useDialog';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';
// Shared with the invoice items table, so a syrup and a capsule do not print the same icon.
import { MEDICINE_TYPE_CONFIG } from '../constants/medicineTypes';

// BOX is labelled "Pack" — the counter word for the retail carton. The key stays BOX so stored
// medicines need no migration; only the wording changes. Mirrors Medicine.PackagingType.
const PACKAGING_TYPE_CONFIG = { AMPOULE: 'Ampoule', BOTTLE: 'Bottle', BOX: 'Pack', SACHET: 'Sachet', STRIP: 'Strip', TUBE: 'Tube', VIAL: 'Vial', OTHER: 'Other' };

// Which packaging options make sense for each medicine type, and the sensible default. Keeps the
// Packaging dropdown from offering "Strip" for a syrup, etc. Unlisted types fall back to all.
const TYPE_PACKAGING = {
  TABLET:    { options: ['BOX', 'STRIP'],            default: 'BOX' },
  CAPSULE:   { options: ['BOX', 'STRIP'],            default: 'BOX' },
  SYRUP:     { options: ['BOTTLE'],                  default: 'BOTTLE' },
  SUSPENSION:{ options: ['BOTTLE'],                  default: 'BOTTLE' },
  DROPS:     { options: ['BOTTLE'],                  default: 'BOTTLE' },
  INJECTION: { options: ['VIAL', 'AMPOULE'],         default: 'VIAL' },
  CREAM:     { options: ['TUBE'],                    default: 'TUBE' },
  OINTMENT:  { options: ['TUBE'],                    default: 'TUBE' },
  POWDER:    { options: ['SACHET', 'BOTTLE', 'BOX'], default: 'SACHET' },
  GEL:       { options: ['TUBE', 'BOX'],             default: 'TUBE' },
  LOTION:    { options: ['BOTTLE'],                  default: 'BOTTLE' },
  SACHET:    { options: ['BOX', 'SACHET'],           default: 'BOX' },
  SPRAY:     { options: ['BOTTLE'],                  default: 'BOTTLE' },
  INHALER:   { options: ['BOX', 'OTHER'],            default: 'BOX' },
  SUPPOSITORY:{ options: ['BOX', 'STRIP'],           default: 'BOX' },
  PATCH:     { options: ['BOX', 'SACHET'],           default: 'BOX' },
  // A solution is bought in a bottle, or in a vial or ampoule when it is a nebuliser dose.
  SOLUTION:  { options: ['BOTTLE', 'VIAL', 'AMPOULE'], default: 'BOTTLE' },
  // Consumable supplies. Without these the map falls through to "every packaging type", which
  // offered a syringe an Ampoule, a Bottle, a Tube and a Vial — none of which it comes in — and
  // made the list long enough to need scrolling in a short modal.
  SYRINGE:    { options: ['BOX', 'OTHER'],            default: 'BOX' },
  GLOVES:     { options: ['BOX'],                     default: 'BOX' },
  TEST_STRIP: { options: ['BOTTLE', 'BOX'],           default: 'BOTTLE' },
  DRESSING:   { options: ['BOX', 'SACHET'],           default: 'BOX' },
  MASK:       { options: ['BOX'],                     default: 'BOX' },
  TUBING:     { options: ['BOX', 'OTHER'],            default: 'BOX' },
  SUTURE:     { options: ['BOX', 'SACHET'],           default: 'BOX' },
  CONSUMABLE: { options: ['BOX', 'OTHER'],            default: 'BOX' },
};
const packagingOptionsFor = (type) => TYPE_PACKAGING[type]?.options || Object.keys(PACKAGING_TYPE_CONFIG);
const defaultPackagingFor = (type) => TYPE_PACKAGING[type]?.default || 'OTHER';

// Liquid types that can be repackaged from a distributor container (e.g. 120 ml) into smaller
// custom dispensing bottles (e.g. 20 ml). Drives the Repackaging section in the medicine form.
export const REPACKAGEABLE_TYPES = new Set(['SYRUP', 'SUSPENSION', 'DROPS', 'SOLUTION']);

// Solid forms bought by the pack/strip but dispensed by the tablet or capsule. The pack holds a
// countable number of units, so the count itself is the conversion factor.
// Bought by the pack, used one at a time — so the pack count IS the conversion factor and the
// stock receipt can be entered pack-first ("4 packs @ Rs 900" -> 400 syringes). Consumables work
// exactly like tablets here: a box of 100 syringes is a box of 100 countable things.
export const SOLID_PACK_TYPES = new Set([
  'TABLET', 'CAPSULE', 'SACHET',
  'SYRINGE', 'GLOVES', 'TEST_STRIP', 'DRESSING', 'MASK', 'TUBING', 'SUTURE', 'CONSUMABLE',
]);
export const SOLID_PACK_PACKAGING = new Set(['STRIP', 'BOX']);

/** What one unit of a supply is called, for "N per pack" and the receipt row. */
const SUPPLY_UNIT_NOUN = {
  SYRINGE: 'Syringes',
  GLOVES: 'Gloves',
  TEST_STRIP: 'Strips',
  DRESSING: 'Dressings',
  MASK: 'Masks',
  TUBING: 'Pieces',
  SUTURE: 'Sutures',
  CONSUMABLE: 'Pieces',
};
/** "Tablets per pack", "Capsules per strip", … for the medicine form and the receipt row. */
export const solidUnitNoun = (type) =>
  SUPPLY_UNIT_NOUN[type] || (type === 'CAPSULE' ? 'Capsules' : 'Tablets');
export const solidPackNoun = (packaging) => (packaging === 'BOX' ? 'pack' : 'strip');

// These five are disjoint buckets that add up to the whole catalog, so the labels have to read as
// bands rather than as claims about having stock. "In Stock" was the odd one out: a Low Stock
// medicine is also in stock, so a card reading "In Stock 90" next to "Low Stock 52" looked like it
// was contradicting itself. "Well Stocked" says what the band actually means, which is above the
// reorder threshold.
const STATUS_CONFIG = {
  IN_STOCK: { label: 'Well Stocked', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  LOW_STOCK: { label: 'Low Stock', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  OUT_OF_STOCK: { label: 'Out of Stock', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  NEAR_EXPIRY: { label: 'Near Expiry', color: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500' },
  EXPIRED: { label: 'Expired', color: 'bg-red-600 text-white font-bold ring-2 ring-red-300', dot: 'bg-white' }
};

/**
 * Compute the medicine's current effective status from its data — NOT from the stored enum.
 *
 * The backend has an @PreUpdate trigger + a 1AM scheduled job that updates the `status` column,
 * but between updates the stored value can be stale (e.g. a medicine created months ago with
 * IN_STOCK whose expiry date has now passed). The stats endpoint queries by date/quantity
 * directly, which is why stats and filters used to disagree.
 *
 * This helper mirrors the backend's priority order so the UI is always self-consistent.
 */
const getEffectiveStatus = (m) => {
  if (!m) return 'IN_STOCK';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (m.expiryDate) {
    // Date-only field. Parsing with new Date() puts it on the previous local day west of UTC,
    // and the setHours below then locks that wrong day in, marking stock EXPIRED a day early.
    // Null, not an Invalid Date, when the value is unparseable — so it must be checked before
    // use rather than left to fail a comparison harmlessly.
    const exp = toClinicDate(m.expiryDate);
    if (exp) {
      exp.setHours(0, 0, 0, 0);
      if (exp < today) return 'EXPIRED';
      const daysToExpiry = (exp - today) / (1000 * 60 * 60 * 24);
      if (daysToExpiry <= 30) return 'NEAR_EXPIRY';
    }
  }
  if ((m.quantity ?? 0) === 0) return 'OUT_OF_STOCK';
  if (m.threshold != null && m.quantity != null && m.quantity <= m.threshold) return 'LOW_STOCK';
  return 'IN_STOCK';
};

// Supplies (syringes, gloves, test strips) share this page's machinery entirely: they are stocked,
// batched, expiry-tracked, received on stock receipts and billed exactly like a medicine. Only the
// wording, the offered types and a few medicine-only fields differ, so the page is parameterised by
// category rather than copied.
const CATALOGUE = {
  MEDICINE: {
    title: 'Medicine Inventory',
    subtitle: 'Medicine catalog and stock levels. Stock arrives through Stock Receipts.',
    addLabel: 'Register Medicine',
    editLabel: 'Edit Medicine',
    empty: 'No medicines found',
    // What one row IS, for the column header and anything else naming a single item. The
    // Supplies page runs on this same component, so "Medicine" cannot be written into it.
    itemLabel: 'Medicine',
    noun: 'medicine',
    nounPlural: 'medicines',
    types: ['TABLET', 'CAPSULE', 'SYRUP', 'INJECTION', 'CREAM', 'OINTMENT', 'GEL', 'DROPS',
            'INHALER', 'POWDER', 'SUSPENSION', 'SOLUTION', 'LOTION', 'SPRAY', 'SUPPOSITORY',
            'PATCH', 'SACHET', 'OTHER'],
  },
  SUPPLY: {
    title: 'Medical Supplies',
    subtitle: 'Consumables and disposables. Stock arrives through Stock Receipts, same as medicines.',
    addLabel: 'Register Supply',
    editLabel: 'Edit Supply',
    empty: 'No supplies found',
    itemLabel: 'Supply',
    noun: 'supply',
    nounPlural: 'supplies',
    types: ['SYRINGE', 'GLOVES', 'TEST_STRIP', 'DRESSING', 'MASK', 'TUBING', 'SUTURE',
            'CONSUMABLE', 'OTHER'],
  },
};

const MedicinesManagement = ({ hasPermission, category = 'MEDICINE' }) => {
  const catalogue = CATALOGUE[category] || CATALOGUE.MEDICINE;
  const showToast = useToast();
  const confirm = useConfirm();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PHARMACY_MEDICINES', action) : true),
    [hasPermission],
  );
  const [medicines, setMedicines] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // A discontinued item is kept only so its history stays readable. It cannot be prescribed,
  // ordered or received, so it is out of the way until asked for. Three positions rather than a
  // switch, because "which ones did we stop stocking?" is a real question and hiding them by
  // default leaves no way to ask it.
  const [discontinuedFilter, setDiscontinuedFilter] = useState('HIDE');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Details carried over when adding a new bottle size as its own medicine record.
  const [variantSeed, setVariantSeed] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [activeOrderItems, setActiveOrderItems] = useState(new Set());
  // "Update Stock" picker — when the user clicks the action on a row, this holds the medicine
  // whose receipts we're picking from. Set null to close the modal.
  const [updateStockFor, setUpdateStockFor] = useState(null);

  // Everything on the page reads from here, so the stat cards, the filters and the rows can never
  // be counting different sets. Toggling discontinued back on widens all three at once.
  const visibleMedicines = React.useMemo(() => {
    if (discontinuedFilter === 'INCLUDE') return medicines;
    const wanted = discontinuedFilter === 'ONLY';
    return medicines.filter(m => !!m.discontinued === wanted);
  }, [medicines, discontinuedFilter]);
  // Counted off the FULL list: the point of the number is to say what is being held back.
  const discontinuedCount = React.useMemo(
    () => medicines.filter(m => m.discontinued).length, [medicines]);

  // Compute stats from the same source the filter uses — guarantees stats and filter always
  // agree. No separate API call, no dependency on the stored `status` column being fresh.
  const stats = React.useMemo(() => {
    let inStock = 0, lowStock = 0, outOfStock = 0, nearExpiry = 0, expired = 0;
    let totalValue = 0;
    for (const m of visibleMedicines) {
      switch (getEffectiveStatus(m)) {
        case 'IN_STOCK':     inStock++;     break;
        case 'LOW_STOCK':    lowStock++;    break;
        case 'OUT_OF_STOCK': outOfStock++;  break;
        case 'NEAR_EXPIRY':  nearExpiry++;  break;
        case 'EXPIRED':      expired++;     break;
        default: break;
      }
      totalValue += Number(m.quantity || 0) * Number(m.purchasePrice || 0);
    }
    return { total: visibleMedicines.length, inStock, lowStock, outOfStock, nearExpiry, expired, totalValue };
  }, [visibleMedicines]);

  // Depends on category as well as mount: App.js keys this component per catalogue so it remounts,
  // but if it is ever rendered without that key, switching catalogue must still refetch rather than
  // leave the other one's rows on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [category]);

  const loadData = async () => {
    setLoading(true); setError(null);
    try {
      // /with-inventory returns each medicine pre-enriched with batch aggregates (total qty,
      // weighted avg / min / max selling price, earliest expiring batch). Avoids per-row
      // round-trips and lets the row component render the multi-batch story directly.
      const [medsData, vendorsData] = await Promise.all([medicineAPI.getAllWithInventory(category), vendorAPI.getAll()]);
      // The new DTO uses `totalQuantity` and `averageSellingPrice` (named for history: it now
      // carries the latest delivery's price, not an average). Map those to the legacy
      // field names existing helpers (getEffectiveStatus, ViewModal, etc.) expect, so the
      // rest of the page doesn't need to know about the rename.
      const enriched = (medsData || []).map(m => ({
        ...m,
        quantity: m.totalQuantity,
        sellingPrice: m.averageSellingPrice,
        expiryDate: m.earliestExpiry,
      }));
      setMedicines(enriched); setVendors(vendorsData || []);
      try { const drafts = await plannerAPI.getDrafts(); setActiveOrderItems(new Set((drafts || []).flatMap(o => (o.items || []).map(i => i.medicine?.id)))); } catch { setActiveOrderItems(new Set()); }
    } catch { setError('Failed to load data.'); }
    finally { setLoading(false); }
  };

  const filteredMedicines = visibleMedicines.filter(m => {
    // The dosage form is searched as well as the name, generic and code. Typing "syrup" used to find
    // nothing while "syr" found the syrups — and only because it happened to match MED-SYR-0002.
    // Both the label and the raw value are matched, so "test strip" and "TEST_STRIP" both work.
    const typeLabel = MEDICINE_TYPE_CONFIG[m.medicineType]?.label;
    const search = !searchTerm
      || [m.name, m.genericName, m.medicineCode, typeLabel, m.medicineType]
        .some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!search) return false;
    if (typeFilter !== 'ALL' && m.medicineType !== typeFilter) return false;
    if (statusFilter !== 'ALL' && getEffectiveStatus(m) !== statusFilter) return false;
    return true;
  });

  // Sorted last, so it orders whatever survived the filters. Qty, Price and Expiry read the same
  // fields the row prints - the total across batches, the latest delivery's price, and the
  // earliest expiry - rather than the formatted text in the cell.
  const { sortConfig, handleSort } = useSort();
  const sortedMedicines = React.useMemo(() => sortRows(filteredMedicines, sortConfig, {
    name:   (m) => m.name,
    type:   (m) => (MEDICINE_TYPE_CONFIG[m.medicineType] || MEDICINE_TYPE_CONFIG.OTHER).label,
    qty:    (m) => Number(m.totalQuantity ?? m.quantity ?? 0),
    price:  (m) => Number(m.averageSellingPrice ?? m.sellingPrice ?? 0),
    expiry: (m) => m.earliestExpiry || m.expiryDate,
    status: (m) => (STATUS_CONFIG[getEffectiveStatus(m)] || STATUS_CONFIG.IN_STOCK).label,
  }), [filteredMedicines, sortConfig]);
  const paging = usePagedRows(sortedMedicines, {
    id: category === 'MEDICINE' ? 'medicines' : 'medical-supplies',
    resetOn: [sortConfig, searchTerm, statusFilter, discontinuedFilter, typeFilter, category],
  });

  const handleCreate = async (data, vendorId) => { try { await medicineAPI.create(data, vendorId); setShowAddModal(false); loadData(); showToast('success', 'Medicine added'); } catch (err) { showToast('error', 'Failed to add medicine: ' + err.message); } };
  // Server-side business rules (e.g. blocking a pack/dispense change while stock is on the shelf)
  // come back as a complete, user-ready sentence — surface it as-is rather than burying it behind
  // a generic prefix.
  const handleUpdate = async (data, vendorId, allowRepackChange = false) => { try { await medicineAPI.update(selectedMedicine.id, data, vendorId, allowRepackChange); setShowEditModal(false); setSelectedMedicine(null); loadData(); showToast('success', 'Medicine updated'); } catch (err) { showToast('error', err.message || 'Failed to update medicine'); } };
  // Hard delete is only possible while nothing references the medicine (registered by mistake,
  // never used). Once it's on a prescription, stock receipt, purchase order or the stock ledger
  // the row can't go without taking that history with it, so ask the server first and steer the
  // user to "discontinue" instead of letting them confirm a delete that will fail.
  const handleDelete = async (m) => {
    let usage = null;
    try { usage = await medicineAPI.getUsage(m.id); }
    catch { /* usage check unavailable — fall through to the plain delete, the server still guards it */ }

    if (usage && !usage.canDelete) {
      if (usage.discontinued) {
        showToast('warning', `"${m.name}" is used in ${usage.summary.toLowerCase()} and cannot be deleted. It is already marked discontinued.`);
        return;
      }
      const discontinueInstead = await confirm({
        title: 'This medicine cannot be deleted',
        subtitle: `Used in ${usage.summary.toLowerCase()}`,
        message: `"${m.name}" is part of existing records, so deleting it would break them. Mark it discontinued instead: it stays out of prescription search and all history is kept.`,
        confirmText: 'Mark discontinued',
        cancelText: 'Cancel',
        tone: 'warning',
      });
      if (!discontinueInstead) return;
      try { await medicineAPI.setDiscontinued(m.id, true); loadData(); showToast('success', `"${m.name}" marked discontinued`); }
      catch (err) { showToast('error', err.message || 'Failed to mark discontinued'); }
      return;
    }

    const ok = await confirm({
      title: `Delete ${catalogue.itemLabel}`,
      subtitle: 'This action cannot be undone',
      message: `Are you sure you want to delete "${m.name}"?`,
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    // Server-side guards come back as complete, user-ready sentences — surface as-is.
    try { await medicineAPI.delete(m.id); loadData(); showToast('success', `"${m.name}" deleted`); }
    catch (err) { showToast('error', err.message || 'Failed to delete medicine'); }
  };

  const handleAddToOrder = async (medicineId) => {
    try { const o = await plannerAPI.addToDraft(null, [{ medicineId, packs: null }]); setActiveOrderItems(prev => new Set([...prev, medicineId])); showToast('success', `Added to ${o.status === 'DRAFT' ? 'draft' : 'order'} ${o.orderNumber} for ${o.vendor?.vendorName || 'its supplier'}${o.status === 'IN_REVIEW' || o.status === 'REVIEWED' ? ', out for review' : ''}`); }
    catch (err) { showToast('error', err.message || 'Could not add it to an order'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center"><Pill className="h-7 w-7 text-white" /></div>
                <div><h1 className="text-3xl font-bold text-gray-900">{catalogue.title}</h1><p className="text-gray-600">{catalogue.subtitle}</p></div>
              </div>
              <button onClick={() => setShowAddModal(true)} className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"><Plus className="h-5 w-5" /><span>{catalogue.addLabel}</span></button>
            </div>
          </div>
        </div>

        {/* Stats — each card filters the table by status. Total clears the filter. */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          {[
            { icon: Package,       label: 'Total',       value: stats.total,       gradient: 'from-blue-500 to-indigo-600',   status: 'ALL' },
            { icon: CheckCircle,   label: 'Well Stocked', value: stats.inStock,    gradient: 'from-emerald-500 to-green-600', status: 'IN_STOCK' },
            { icon: TrendingDown,  label: 'Low Stock',   value: stats.lowStock,    gradient: 'from-amber-500 to-orange-600',  status: 'LOW_STOCK' },
            { icon: AlertCircle,   label: 'Out of Stock', value: stats.outOfStock, gradient: 'from-red-500 to-rose-600',      status: 'OUT_OF_STOCK' },
            { icon: Clock,         label: 'Near Expiry', value: stats.nearExpiry,  gradient: 'from-orange-500 to-amber-600',  status: 'NEAR_EXPIRY' },
            { icon: AlertTriangle, label: 'Expired',     value: stats.expired,     gradient: 'from-gray-500 to-slate-600',    status: 'EXPIRED' },
          ].map((s, i) => {
            const isActive = statusFilter === s.status && s.status !== 'ALL';
            const toggle = () => {
              if (s.status === 'ALL') setStatusFilter('ALL');
              else setStatusFilter(isActive ? 'ALL' : s.status);
            };
            return (
              <button
                key={i}
                type="button"
                onClick={toggle}
                title={s.status === 'ALL' ? 'Show all medicines' : isActive ? 'Clear filter' : `Filter by ${s.label}`}
                className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg text-left transition-all ${
                  isActive ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                } ${s.status === 'ALL' && statusFilter === 'ALL' ? 'ring-2 ring-blue-400' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-xs">{s.label}</p>
                    <p className="text-xl font-bold text-gray-900">{s.value || 0}</p>
                  </div>
                  <div className={`w-10 h-10 bg-gradient-to-br ${s.gradient} rounded-lg flex items-center justify-center`}>
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative"><Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" /><input type="text" placeholder="Search by name, generic name, or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" /></div>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: 'ALL', label: 'All Status' }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            className="min-w-[10rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            options={[{ value: 'ALL', label: 'All Types' }, ...catalogue.types
              .filter(k => MEDICINE_TYPE_CONFIG[k])
              .map(k => ({ value: k, label: MEDICINE_TYPE_CONFIG[k].label }))]}
            className="min-w-[10rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          {/* Only offered when there is something to reveal, so the control does not sit there
              promising rows that do not exist. The count is on the face of it for the same reason:
              nobody should have to switch it on to find out whether anything is hidden. */}
          {discontinuedCount > 0 && (
            <Select
              value={discontinuedFilter}
              onChange={setDiscontinuedFilter}
              options={[
                { value: 'HIDE', label: `Hide discontinued (${discontinuedCount})` },
                { value: 'INCLUDE', label: 'Include discontinued' },
                { value: 'ONLY', label: 'Discontinued only' },
              ]}
              className="min-w-[13rem]"
              buttonClassName="px-4 py-3 rounded-xl"
            />
          )}
          <button title="Refresh" onClick={loadData} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50"><RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>

        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><span className="text-red-700">{error}</span></div>}

        {/* Table */}
        {loading ? <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        : filteredMedicines.length === 0 ? <div className="bg-white rounded-2xl shadow-xl p-12 text-center"><Pill className="h-16 w-16 text-gray-300 mx-auto mb-4" /><h3 className="text-xl font-semibold text-gray-500">{catalogue.empty}</h3><button onClick={() => setShowAddModal(true)} className="mt-4 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700">{catalogue.addLabel}</button></div>
        : <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <TablePager paging={paging} noun={catalogue.noun} nounPlural={catalogue.nounPlural} filteredFrom={visibleMedicines.length} />
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader label={catalogue.itemLabel} sortKey="name" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-4" />
                <SortHeader label="Type" sortKey="type" sortConfig={sortConfig} onSort={handleSort} className="px-4 py-4" />
                <SortHeader label="Qty" sortKey="qty" sortConfig={sortConfig} onSort={handleSort} align="center" className="px-4 py-4" />
                <SortHeader label="Price" sortKey="price" sortConfig={sortConfig} onSort={handleSort} align="right" className="px-4 py-4" />
                <SortHeader label="Expiry" sortKey="expiry" sortConfig={sortConfig} onSort={handleSort} align="center" className="px-4 py-4" />
                <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} align="center" className="px-4 py-4" />
                <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {paging.pageRows.map(m => <MedicineRow key={m.id} medicine={m} inOrder={activeOrderItems.has(m.id)} can={can} onView={() => { setSelectedMedicine(m); setShowViewModal(true); }} onEdit={() => { setSelectedMedicine(m); setShowEditModal(true); }} onDelete={() => handleDelete(m)} onAddToOrder={() => handleAddToOrder(m.id)} onUpdateStock={() => setUpdateStockFor(m)} />)}
              </tbody>
            </table>

            {(searchTerm || statusFilter !== 'ALL' || typeFilter !== 'ALL') && (
              <div className="px-6 py-2 border-t border-gray-100 text-sm text-right">
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('ALL'); setTypeFilter('ALL'); }}
                  className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
            {/* The count is repeated where scrolling ends, with the page buttons. */}
            <TablePager paging={paging} noun={catalogue.noun} nounPlural={catalogue.nounPlural} filteredFrom={visibleMedicines.length} position="bottom" />
          </div>}
      </div>

      {showAddModal && <MedicineFormModal category={category} medicine={variantSeed} isVariant={!!variantSeed} vendors={vendors} onClose={() => { setShowAddModal(false); setVariantSeed(null); }} onSubmit={handleCreate} />}
      {showEditModal && selectedMedicine && (
        <MedicineFormModal
          category={category}
          medicine={selectedMedicine}
          vendors={vendors}
          onClose={() => { setShowEditModal(false); setSelectedMedicine(null); }}
          onSubmit={handleUpdate}
          // A new dispensing size is a new product: carry this medicine's details into a fresh
          // record at zero stock rather than relabelling bottles that are already filled.
          onCreateVariant={(seed) => { setShowEditModal(false); setSelectedMedicine(null); setVariantSeed(seed); setShowAddModal(true); }}
        />
      )}
      {showViewModal && selectedMedicine && (
        <ViewModal
          medicine={selectedMedicine}
          itemLabel={catalogue.itemLabel}
          onClose={() => { setShowViewModal(false); setSelectedMedicine(null); }}
          onChanged={loadData}
          // Hand straight over to the edit form, keeping the same medicine selected.
          onEdit={can('EDIT') ? () => { setShowViewModal(false); setShowEditModal(true); } : null}
        />
      )}
      {updateStockFor && (
        <UpdateStockModal medicine={updateStockFor} onClose={() => setUpdateStockFor(null)} />
      )}
    </div>
  );
};

const MedicineRow = ({ medicine, inOrder, can, onView, onEdit, onDelete, onAddToOrder, onUpdateStock }) => {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  const tc = MEDICINE_TYPE_CONFIG[medicine.medicineType] || MEDICINE_TYPE_CONFIG.OTHER;
  const effectiveStatus = getEffectiveStatus(medicine);
  const sc = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.IN_STOCK;
  const isExpired = effectiveStatus === 'EXPIRED';
  const isLow = effectiveStatus === 'LOW_STOCK';
  
  return (
    // Row click opens the View modal — the same action the row's "View" menu item
    // triggers. RowActionsMenu already stops propagation on its trigger + items so
    // clicks inside the Actions cell don't double-fire as a row click.
    <tr tabIndex={0} onClick={onView} onKeyDown={onActivateKey(onView)} className={`hover:bg-gray-50/50 cursor-pointer ${ROW_FOCUS_RING}`}>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 bg-gradient-to-br ${tc.color} rounded-lg flex items-center justify-center`}><tc.icon className="h-5 w-5 text-white" /></div>
          <div>
            <div className="font-semibold text-gray-900">{medicine.name}</div>
            {medicine.genericName && (
              <div className="text-xs text-gray-600 italic">{medicine.genericName}</div>
            )}
            <div className="text-xs text-gray-400">{medicine.medicineCode}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="text-sm">{tc.label}</div>
        {(medicine.strength || medicine.containerSize) && (
          <div className="text-xs text-gray-400 mt-0.5">
            {[medicine.strength, medicine.containerSize].filter(Boolean).join(' · ')}
          </div>
        )}
        {medicine.unitsPerPack > 1 && medicine.packSize && medicine.dispenseSize && (
          <div
            className="mt-1 inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-100"
            title={`Repackaged: a ${medicine.packSize}${medicine.sizeUnit} bottle is dispensed as ${medicine.unitsPerPack} × ${medicine.dispenseSize}${medicine.sizeUnit} bottles (stock & price are per ${medicine.dispenseSize}${medicine.sizeUnit} bottle)`}
          >
            {medicine.packSize}{medicine.sizeUnit} → {medicine.dispenseSize}{medicine.sizeUnit} · {medicine.unitsPerPack}/btl
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-center">
        <div className={`font-semibold ${isLow ? 'text-amber-600' : ''}`}>{medicine.totalQuantity ?? medicine.quantity ?? 0}</div>
        {medicine.activeBatchCount > 1 && (
          <div className="text-[10px] text-gray-400 mt-0.5">{medicine.activeBatchCount} batches</div>
        )}
      </td>
      <td className="px-4 py-4 text-right">
        {(() => {
          // The headline is the latest delivery's price, which is what every unit in stock is sold
          // at and what the invoice charges. The min-max line underneath is only there to show that
          // earlier batches came in at other prices, so an unintended jump is visible.
          const price = medicine.averageSellingPrice ?? medicine.sellingPrice;
          const min = medicine.minSellingPrice;
          const max = medicine.maxSellingPrice;
          const showRange = min != null && max != null && Number(min) !== Number(max);
          return (
            <>
              <div className="text-sm font-medium">Rs. {price != null ? Number(price).toFixed(0) : '—'}</div>
              {showRange ? (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  latest &middot; batches Rs. {Number(min).toFixed(0)} – {Number(max).toFixed(0)}
                </div>
              ) : medicine.activeBatchCount > 1 ? (
                <div className="text-[10px] text-gray-400 mt-0.5">latest of {medicine.activeBatchCount} batches</div>
              ) : null}
            </>
          );
        })()}
      </td>
      <td className="px-4 py-4 text-center">
        {(() => {
          const exp = medicine.earliestExpiry || medicine.expiryDate;
          if (!exp) return <span className="text-gray-400">-</span>;
          const dateStr = formatClinicDate(exp, { day: '2-digit', month: 'short', year: 'numeric' });
          const expired = medicine.hasExpiredBatch || isExpired;
          const nearExpiry = !expired && medicine.hasNearExpiry;
          const cls = expired ? 'text-red-600 font-semibold' : nearExpiry ? 'text-orange-600 font-medium' : 'text-gray-700';
          return (
            <>
              <div className={`text-sm ${cls}`}>{dateStr}</div>
              {medicine.activeBatchCount > 1 && medicine.earliestExpiryBatch && (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Batch <span className="font-mono">{medicine.earliestExpiryBatch}</span>
                  {medicine.earliestExpiryQuantity != null && (
                    <span> · {medicine.earliestExpiryQuantity} units</span>
                  )}
                </div>
              )}
              {medicine.activeBatchCount > 1 && !medicine.earliestExpiryBatch && (
                <div className="text-[10px] text-gray-400 mt-0.5">earliest of {medicine.activeBatchCount} batches</div>
              )}
            </>
          );
        })()}
      </td>
      <td className="px-4 py-4 text-center">
        <div className="flex flex-col items-center gap-1">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}><span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${sc.dot}`}></span>{sc.label}</span>
          {medicine.discontinued && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-700">Discontinued</span>
          )}
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center justify-end">
          <RowActionsMenu items={[
            { key: 'view',        label: 'View',                icon: Eye,        iconClass: 'text-blue-500',    onClick: onView },
            allowed('EDIT') && { key: 'edit',        label: 'Edit catalog info',   icon: Edit2,      iconClass: 'text-emerald-500', onClick: onEdit },
            allowed('UPDATE_STOCK') && { key: 'update',      label: 'Update Stock',        icon: PackagePlus, iconClass: 'text-teal-600',   subtitle: 'From an existing receipt', onClick: onUpdateStock },
            inOrder
              ? { key: 'inorder', label: 'Already in order',    icon: CheckCircle, iconClass: 'text-emerald-500', disabled: true }
              : { key: 'order',   label: 'Add to Order',        icon: ShoppingCart, iconClass: 'text-orange-500', onClick: onAddToOrder },
            allowed('DELETE') && { key: 'delete',      label: 'Delete',              icon: Trash2,     danger: true,                  onClick: onDelete },
          ].filter(Boolean)} />
        </div>
      </td>
    </tr>
  );
};

// Exported so other surfaces (e.g. the Stock Receipt "add new medicine" inline flow) can
// reuse the exact same add-medicine form instead of duplicating it. `initialName` pre-seeds
// the Name field (used when the user typed an unknown medicine name into a search box).
export const MedicineFormModal = ({ medicine, vendors, onClose, onSubmit, initialName = '', onCreateVariant = null, isVariant = false, category = 'MEDICINE' }) => {
  // A supply is the same record with different fields filled in: no generic name, no strength, no
  // dosage form to speak of. The catalogue decides the wording and which forms are offered.
  const catalogue = CATALOGUE[category] || CATALOGUE.MEDICINE;
  const isSupply = category === 'SUPPLY';
  // A variant prefills from an existing medicine but is a brand-new record: its own code, its own
  // stock starting at zero, its own prices. So it is a create, not an edit.
  const isEdit = !!medicine && !isVariant;
  // Form fields are catalog metadata only — inventory state (quantity, purchase price,
  // vendor, batch, manufacture/expiry dates) lives on Stock Receipts now and is rolled up
  // onto the medicine record by StockReceiptService. When editing an existing medicine we
  // preserve those rollup values invisibly; when adding new we default them to zero/null
  // and they get populated on the first Stock Receipt.
  const [form, setForm] = useState({
    name: medicine?.name || initialName || '',
    genericName: medicine?.genericName || '',
    manufacturer: medicine?.manufacturer || '',
    medicineType: medicine?.medicineType || catalogue.types[0],
    category,
    packagingType: medicine?.packagingType || defaultPackagingFor(medicine?.medicineType || 'TABLET'),
    strength: medicine?.strength || '',
    containerSize: medicine?.containerSize || '',
    packSize: medicine?.packSize != null ? String(medicine.packSize) : '',
    dispenseSize: medicine?.dispenseSize != null ? String(medicine.dispenseSize) : '',
    sizeUnit: medicine?.sizeUnit || 'ml',
    // Tablets/capsules per pack or strip. Blank or 1 means it is bought and dispensed loose.
    unitsPerPack: medicine?.unitsPerPack > 1 ? String(medicine.unitsPerPack) : '',
    threshold: medicine?.threshold?.toString() || '10',
    storageLocation: medicine?.storageLocation || '',
    notes: medicine?.notes || '',
  });
  const [errors, setErrors] = useState({});

  // Repackaging is only offered for liquid types. Derived conversion factor (units per received
  // pack) = floor(received size / dispense size), e.g. 120 / 20 = 6.
  const isRepackType = REPACKAGEABLE_TYPES.has(form.medicineType);
  const packSizeNum = parseFloat(form.packSize);
  const dispenseSizeNum = parseFloat(form.dispenseSize);
  const repackFilled = isRepackType && (form.packSize !== '' || form.dispenseSize !== '');
  const repackValid =
    isRepackType && packSizeNum > 0 && dispenseSizeNum > 0 && dispenseSizeNum <= packSizeNum;
  const derivedLiquidUnitsPerPack = repackValid ? Math.max(1, Math.floor(packSizeNum / dispenseSizeNum)) : 1;

  // Solids arrive in a pack or strip holding a countable number of tablets/capsules. Same idea as
  // liquid repackaging — one purchased container becomes N dispensing units — but there is no
  // "size" to divide, the count IS the factor. Both paths feed the same unitsPerPack, which is what
  // the stock receipt uses to turn "5 strips @ Rs 200" into 50 tablets at Rs 20 each.
  const isSolidPackType = SOLID_PACK_TYPES.has(form.medicineType)
    && SOLID_PACK_PACKAGING.has(form.packagingType);
  const unitsPerPackNum = parseInt(form.unitsPerPack, 10);
  const solidPackValid = isSolidPackType && unitsPerPackNum > 1;
  // Injections, creams, inhalers and the rest have no count on this form, yet they can carry one
  // (the opening stock load gave 20 injections "50 per pack" and the like) and stock receipts
  // convert packs with it. Saving the form must keep it, not quietly reset it to 1.
  const formSetsPackCount = isRepackType || SOLID_PACK_TYPES.has(form.medicineType);
  const derivedUnitsPerPack = repackValid
    ? derivedLiquidUnitsPerPack
    : solidPackValid
      ? unitsPerPackNum
      : isEdit && !formSetsPackCount
        ? (medicine?.unitsPerPack || 1)
        : 1;
  // Stock is counted in dispensing units, so redefining what a dispensing unit is while stock sits
  // on the shelf re-interprets that stock. It must NOT be forbidden though — a syrup you already
  // stock is exactly the one you most often need to start repackaging — so instead of locking the
  // inputs we show what will happen to the quantity and require an explicit acknowledgement.
  // A variant starts empty even though it prefills from a medicine that has stock, so the
  // stock-on-hand warnings must not fire for it.
  const stockOnHand = isEdit ? (medicine?.quantity ?? 0) : 0;
  // Only the two sizes count, exactly as the server's own test: a pack count is a purchase
  // conversion and never re-reads the stock on the shelf.
  const repackChanged =
    isEdit &&
    (String(medicine?.packSize ?? '') !== String(repackValid ? packSizeNum : '') ||
      String(medicine?.dispenseSize ?? '') !== String(repackValid ? dispenseSizeNum : ''));
  // Only the LIQUID path needs the stock warning. For tablets the dispensing unit is one tablet
  // either way — saying "10 per strip" only tells the receipt form how to convert a purchase, it
  // does not change what the units already on the shelf are.
  const needsRepackConfirm = repackChanged && stockOnHand > 0 && !isSolidPackType;
  // Redefining the dispensing UNIT itself (20ml -> 40ml) is far more damaging than filling the
  // pack size in for the first time: existing quantity, prices and open batches all keep their old
  // meaning. Called out separately so the two cases don't get the same mild warning.
  const dispenseSizeChanged =
    isEdit &&
    medicine?.dispenseSize != null &&
    String(medicine.dispenseSize) !== String(repackValid ? dispenseSizeNum : '');
  // Redefining an EXISTING dispensing size while bottles are on the shelf is refused outright —
  // the bottles are already filled, so no confirmation can make them a different size.
  const sizeChangeBlocked = dispenseSizeChanged && stockOnHand > 0;
  const [repackConfirmed, setRepackConfirmed] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (repackFilled) {
      if (!(packSizeNum > 0)) e.packSize = 'Enter received size';
      if (!(dispenseSizeNum > 0)) e.dispenseSize = 'Enter dispense size';
      else if (packSizeNum > 0 && dispenseSizeNum > packSizeNum) e.dispenseSize = 'Must be ≤ received size';
    }
    if (isSolidPackType && form.unitsPerPack !== '' && !(unitsPerPackNum >= 1)) {
      e.unitsPerPack = 'Enter a whole number of 1 or more';
    }
    if (sizeChangeBlocked) {
      e.dispenseSize = 'Stock on hand is already bottled at the old size - add a separate medicine instead';
    } else if (needsRepackConfirm && !repackConfirmed) {
      e.repackConfirm = 'Confirm how the stock on hand should be counted before saving';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  // A check whose field this type does not show (a syrup's bottle sizes once its Type is changed to
  // Tablet) is said beside the buttons instead, or Update would do nothing and say nothing.
  const offscreenErrors = [
    !isRepackType && (errors.dispenseSize || errors.packSize || errors.repackConfirm),
    !isSolidPackType && errors.unitsPerPack,
  ].filter(Boolean);

  // A stored packaging the type no longer offers (injections that came in as Pack) still shows,
  // or the field reads "Select..." as though it were empty.
  const packagingChoices = packagingOptionsFor(form.medicineType);
  const packagingOptions = form.packagingType && !packagingChoices.includes(form.packagingType)
    ? [...packagingChoices, form.packagingType]
    : packagingChoices;

  const handleSubmit = () => {
    if (!validate()) return;
    // Preserve inventory + pricing rollup values when editing; default to 0/null for new
    // medicines so backend NOT NULL constraints stay satisfied. Vendor link is intentionally
    // not sent on the metadata path — it's set by Stock Receipts.
    onSubmit({
      ...form,
      threshold: parseInt(form.threshold) || 10,
      // Repackaging: persist the received/dispense sizes + derived factor when valid; otherwise
      // clear them (factor 1). For repackaged liquids the dispensing size doubles as the container
      // size shown elsewhere in the app.
      packSize: repackValid ? packSizeNum : null,
      dispenseSize: repackValid ? dispenseSizeNum : null,
      sizeUnit: repackValid ? form.sizeUnit : null,
      // Both routes converge on unitsPerPack: derived from the sizes for liquids, entered directly
      // as a count for tablets and capsules.
      unitsPerPack: derivedUnitsPerPack,
      containerSize: repackValid ? `${dispenseSizeNum} ${form.sizeUnit}` : form.containerSize,
      // Pass through rollup values untouched on edit. On create (including a new bottle size
      // carried over from another record) these start at zero and the first Stock Receipt fills
      // them in — a new size must never inherit the old size's stock or pricing.
      quantity: isEdit ? (medicine?.quantity ?? 0) : 0,
      purchasePrice: isEdit ? (medicine?.purchasePrice ?? 0) : 0,
      sellingPrice: isEdit ? (medicine?.sellingPrice ?? 0) : 0,
      batchNumber: isEdit ? (medicine?.batchNumber ?? null) : null,
      expiryDate: isEdit ? (medicine?.expiryDate ?? null) : null,
      manufactureDate: isEdit ? (medicine?.manufactureDate ?? null) : null,
    }, medicine?.vendor?.id || null, needsRepackConfirm && repackConfirmed);
  };

  const medicineFormDialogRef = useDialog(onClose);

  return (
    <div ref={medicineFormDialogRef} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              {isEdit ? <Edit2 className="h-6 w-6 text-white" /> : <Plus className="h-6 w-6 text-white" />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{isEdit ? catalogue.editLabel : isVariant ? 'Add New Bottle Size' : catalogue.addLabel}</h2>
              <p className="text-xs text-white/80 mt-0.5">
                {isVariant
                  ? 'A separate record for the new bottle size, starting at zero stock. The existing size keeps its own stock and price until it sells through.'
                  : <>Catalog details only - stock, batch, expiry, purchase price and selling price are tracked under <span className="font-semibold">Stock Receipts</span>.</>}
              </p>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-semibold mb-2">Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} onBlur={e => setForm(f => ({...f, name: toTitleCase(e.target.value)}))} className={`w-full p-3 border-2 rounded-xl ${errors.name ? 'border-red-300' : 'border-gray-200'}`} />
          </div>
          {/* A generic name is a medicine idea: a box of gloves has no active ingredient. */}
          {!isSupply && (
          <div>
            <label className="block text-sm font-semibold mb-2">Generic Name</label>
            <input value={form.genericName} onChange={e => setForm({...form, genericName: e.target.value})} onBlur={e => setForm(f => ({...f, genericName: toTitleCase(e.target.value)}))} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
          </div>
          )}
          <div>
            <label className="block text-sm font-semibold mb-2">Manufacturer</label>
            <input value={form.manufacturer} onChange={e => setForm({...form, manufacturer: e.target.value})} onBlur={e => setForm(f => ({...f, manufacturer: toTitleCase(e.target.value)}))} className="w-full p-3 border-2 border-gray-200 rounded-xl" placeholder="e.g., GSK, Getz Pharma" maxLength={100} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Type</label>
            <Select
              value={form.medicineType}
              onChange={(newType) => {
                // Reset packaging to the new type's default when the current one isn't valid for it.
                setForm(f => {
                  const allowed = packagingOptionsFor(newType);
                  return {
                    ...f,
                    medicineType: newType,
                    packagingType: allowed.includes(f.packagingType) ? f.packagingType : defaultPackagingFor(newType),
                  };
                });
              }}
              options={catalogue.types
                .filter(k => MEDICINE_TYPE_CONFIG[k])
                .map(k => ({ value: k, label: MEDICINE_TYPE_CONFIG[k].label }))}
              buttonClassName="p-3 border-2 rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Packaging</label>
            <Select
              value={form.packagingType}
              onChange={(v) => setForm({ ...form, packagingType: v })}
              options={packagingOptions.map(k => ({ value: k, label: PACKAGING_TYPE_CONFIG[k] || k }))}
              buttonClassName="p-3 border-2 rounded-xl"
            />
          </div>
          {/* Tablets / capsules per pack or strip. Stock is counted in individual tablets, so this
              is what turns a purchase of "5 strips" into 50 tablets at the right per-tablet cost. */}
          {isSolidPackType && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                {solidUnitNoun(form.medicineType)} per {solidPackNoun(form.packagingType)}
              </label>
              <input
                type="number"
                min="1"
                value={form.unitsPerPack}
                onChange={e => { setForm({ ...form, unitsPerPack: e.target.value }); if (errors.unitsPerPack) setErrors(p => ({ ...p, unitsPerPack: null })); }}
                placeholder="e.g., 10"
                className={`w-full p-3 border-2 rounded-xl ${errors.unitsPerPack ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.unitsPerPack ? (
                <p className="text-red-500 text-xs mt-1">{errors.unitsPerPack}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {solidPackValid
                    ? `Buying 1 ${solidPackNoun(form.packagingType)} adds ${unitsPerPackNum} ${solidUnitNoun(form.medicineType).toLowerCase()} to stock.`
                    : `Leave blank if bought and dispensed as single ${solidUnitNoun(form.medicineType).toLowerCase()}.`}
                </p>
              )}
            </div>
          )}
          {/* A strength is a dose. A syringe has a size, not 500mg of anything. */}
          {!isSupply && (
          <div>
            <label className="block text-sm font-semibold mb-2">Strength</label>
            <input value={form.strength} onChange={e => setForm({...form, strength: e.target.value})} className="w-full p-3 border-2 border-gray-200 rounded-xl" placeholder="e.g., 500mg" />
          </div>
          )}
          {/* A supply's own size, which Strength used to be the only home for. A syringe is 3cc /
              5cc / 10cc (cc and ml are the same thing), gloves are Small/Medium/Large, a dressing is
              measured in cm. Free text because the units genuinely differ per type, and optional
              because a mask or a plain consumable often has no size worth recording. Stored in
              containerSize, the same field a bottle's size uses. */}
          {isSupply && (() => {
            const supplySize = {
              SYRINGE:    { label: 'Size',        placeholder: 'e.g., 3cc, 5cc, 10cc' },
              GLOVES:     { label: 'Size',        placeholder: 'e.g., Small, Medium, Large' },
              DRESSING:   { label: 'Size',        placeholder: 'e.g., 10 x 10 cm' },
              TUBING:     { label: 'Size / Gauge', placeholder: 'e.g., 18G, 60 cm' },
              MASK:       { label: 'Size',        placeholder: 'e.g., Adult, Child' },
              TEST_STRIP: { label: 'Size',        placeholder: 'e.g., for Accu-Chek Active' },
              SUTURE:     { label: 'Size / Gauge', placeholder: 'e.g., 3-0, 1 with round body needle' },
            };
            const cfg = supplySize[form.medicineType] || { label: 'Size', placeholder: '' };
            return (
              <div>
                <label className="block text-sm font-semibold mb-2">{cfg.label}</label>
                <input
                  value={form.containerSize}
                  onChange={e => setForm({ ...form, containerSize: e.target.value })}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl"
                  placeholder={cfg.placeholder}
                  maxLength={50}
                />
              </div>
            );
          })()}
          {/* Container size — only meaningful for non-repackaged liquid / cream / sachet packaging.
              Repackaged liquids use the dedicated Repackaging section below instead. */}
          {!isSupply && !isRepackType && (() => {
            const sizeConfig = {
              BOTTLE:   { label: 'Bottle Size',   placeholder: 'e.g., 60 ml, 120 ml' },
              VIAL:     { label: 'Vial Size',     placeholder: 'e.g., 10 ml' },
              AMPOULE:  { label: 'Ampoule Size',  placeholder: 'e.g., 2 ml' },
              TUBE:     { label: 'Tube Size',     placeholder: 'e.g., 30 g, 50 g' },
              SACHET:   { label: 'Sachet Size',   placeholder: 'e.g., 5 g' },
              JAR:      { label: 'Jar Size',      placeholder: 'e.g., 100 g' },
            };
            const cfg = sizeConfig[form.packagingType];
            if (!cfg) return null;
            return (
              <div>
                <label className="block text-sm font-semibold mb-2">{cfg.label}</label>
                <input
                  value={form.containerSize}
                  onChange={e => setForm({...form, containerSize: e.target.value})}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl"
                  placeholder={cfg.placeholder}
                  maxLength={50}
                />
              </div>
            );
          })()}

          {/* Repackaging — for syrups/suspensions/drops received in a large distributor bottle but
              dispensed in smaller custom bottles. Captures both sizes and derives the conversion
              factor. Optional: leave blank for a medicine dispensed in its received bottle. */}
          {isRepackType && (
            <div className="md:col-span-2 lg:col-span-3 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-gray-800">Repackaging</span>
                <span className="text-xs text-gray-500">For splitting a received bottle into smaller dispensing bottles</span>
              </div>
              {/* Bottles are filled on arrival, so stock on the shelf physically IS the old size.
                  A new size is a new product, not a relabelling — steer to a separate record. */}
              {sizeChangeBlocked ? (
                <div className="rounded-lg px-3 py-3 mb-3 border border-red-200 bg-red-50">
                  <p className="text-xs text-red-800">
                    This medicine holds <strong>{stockOnHand} bottle(s) of {medicine.dispenseSize}{medicine.sizeUnit}</strong> that are
                    already filled. Re-labelling them as {repackValid ? `${dispenseSizeNum}${form.sizeUnit}` : 'another size'} would
                    misprice them and make an ml dose hand over the wrong volume.
                  </p>
                  <p className="text-xs text-red-800 mt-1.5">
                    Add a <strong>separate medicine</strong> for the {repackValid ? `${dispenseSizeNum}${form.sizeUnit}` : 'new'} bottle
                    and let this one sell through, then discontinue it. Both sizes can sit on the shelf at the same time, each with its
                    own stock and price.
                  </p>
                  {onCreateVariant && (
                    <button
                      type="button"
                      onClick={() => onCreateVariant({
                        ...medicine,
                        packSize: packSizeNum > 0 ? packSizeNum : medicine.packSize,
                        dispenseSize: dispenseSizeNum > 0 ? dispenseSizeNum : null,
                        sizeUnit: form.sizeUnit,
                      })}
                      className="mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add {repackValid ? `${dispenseSizeNum}${form.sizeUnit}` : 'new size'} as a separate medicine
                    </button>
                  )}
                </div>
              ) : needsRepackConfirm && (
                <div className={`rounded-lg px-3 py-2.5 mb-3 border ${errors.repackConfirm ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                  <p className="text-xs text-amber-800">
                    There {stockOnHand === 1 ? 'is' : 'are'} <strong>{stockOnHand} unit(s)</strong> in stock.
                    Stock is counted in dispensing units, so they will now be read as{' '}
                    <strong>{repackValid ? `${dispenseSizeNum} ${form.sizeUnit}` : 'whole'} bottles</strong>.
                    Check that matches what is on the shelf.
                  </p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={repackConfirmed}
                      onChange={(e) => { setRepackConfirmed(e.target.checked); if (e.target.checked) setErrors(p => ({ ...p, repackConfirm: null })); }}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs font-medium text-amber-900">I understand, apply this change</span>
                  </label>
                  {errors.repackConfirm && <p className="text-red-600 text-xs mt-1">{errors.repackConfirm}</p>}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-start">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Received bottle</label>
                  <input
                    type="number" min="0" step="any"
                    value={form.packSize}
                    onChange={e => setForm({ ...form, packSize: e.target.value })}
                    className={`w-full p-2.5 border-2 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.packSize ? 'border-red-300' : 'border-gray-200'}`}
                    placeholder="e.g., 120"
                  />
                  {errors.packSize && <p className="text-red-500 text-xs mt-1">{errors.packSize}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Dispense as</label>
                  <input
                    type="number" min="0" step="any"
                    value={form.dispenseSize}
                    onChange={e => setForm({ ...form, dispenseSize: e.target.value })}
                    className={`w-full p-2.5 border-2 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.dispenseSize ? 'border-red-300' : 'border-gray-200'}`}
                    placeholder="e.g., 20"
                  />
                  {errors.dispenseSize && <p className="text-red-500 text-xs mt-1">{errors.dispenseSize}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Unit</label>
                  <Select
                    value={form.sizeUnit}
                    onChange={(v) => setForm({ ...form, sizeUnit: v })}
                    options={[{ value: 'ml', label: 'ml' }, { value: 'g', label: 'g' }]}
                    buttonClassName="p-2.5 border-2 rounded-lg"
                  />
                </div>
                <div className="sm:pt-6">
                  {repackValid ? (
                    <span className="inline-flex items-center px-3 py-2 rounded-lg bg-emerald-100 text-emerald-800 text-sm font-semibold">
                      = {derivedUnitsPerPack} units / bottle
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Fill both sizes</span>
                  )}
                </div>
              </div>
              {repackValid && (
                <p className="text-xs text-gray-500 mt-2">
                  Stock, cost and price are tracked per {dispenseSizeNum} {form.sizeUnit} bottle. A received
                  {' '}{packSizeNum} {form.sizeUnit} bottle becomes {derivedUnitsPerPack} dispensing bottles.
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold mb-2">Low-stock Threshold</label>
            <input type="number" min="0" value={form.threshold} onChange={e => setForm({...form, threshold: e.target.value})} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Storage Location</label>
            <input value={form.storageLocation} onChange={e => setForm({...form, storageLocation: e.target.value})} onBlur={e => setForm(f => ({...f, storageLocation: toTitleCase(e.target.value)}))} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
          </div>
        </div>
        {!isEdit && (
          <div className="mx-6 mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800">
            <span className="font-semibold">Next step:</span> after adding this medicine, record your first delivery
            under <span className="font-semibold">Inventory → Stock Receipts</span> to populate quantity, batch number,
            expiry date, purchase price, and selling price.
          </div>
        )}
        <div className="p-6 border-t flex items-center justify-end gap-3 bg-gray-50">
          {offscreenErrors.length > 0 && (
            <p role="alert" className="mr-auto text-sm text-red-600">{offscreenErrors.join(' ')}</p>
          )}
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium">Cancel</button>
          <button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-medium">{isEdit ? 'Update' : 'Register'}</button>
        </div>
      </div>
    </div>
  );
};

const ViewModal = ({ medicine, itemLabel = 'Medicine', onClose, onChanged, onEdit }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [savingDisc, setSavingDisc] = useState(false);
  const discontinued = !!medicine.discontinued;
  const handleToggleDiscontinued = async () => {
    const ok = await confirm({
      title: discontinued ? 'Resume this medicine?' : 'Mark as discontinued?',
      message: discontinued
        ? `"${medicine.name}" will be available for prescriptions again.`
        : `"${medicine.name}" will be marked discontinued - hidden from prescription search. Existing stock and history are kept; you can resume it anytime.`,
      confirmText: discontinued ? 'Resume' : 'Mark discontinued',
      tone: discontinued ? 'default' : 'danger',
    });
    if (!ok) return;
    setSavingDisc(true);
    try {
      await medicineAPI.setDiscontinued(medicine.id, !discontinued);
      showToast('success', discontinued ? `${itemLabel} resumed` : `${itemLabel} marked discontinued`);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      showToast('error', e.message || 'Failed to update');
    } finally {
      setSavingDisc(false);
    }
  };
  const tc = MEDICINE_TYPE_CONFIG[medicine.medicineType] || MEDICINE_TYPE_CONFIG.OTHER;
  const sc = STATUS_CONFIG[getEffectiveStatus(medicine)] || STATUS_CONFIG.IN_STOCK;
  const [tab, setTab] = useState('details'); // 'details' | 'history'
  const [history, setHistory] = useState(null); // null = not yet loaded
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load the stock history eagerly when the modal opens — we need it to compute the
  // Average Purchase Price shown on the Details tab, and the Stock History tab reads
  // from the same data so there's no benefit to deferring it further.
  useEffect(() => {
    if (history !== null) return;
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const data = await stockReceiptAPI.historyForMedicine(medicine.id);
        if (!cancelled) setHistory(data || []);
      } catch (e) {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [history, medicine.id]);

  // Weighted average purchase price across every receipt for this medicine. Weighting
  // by quantityReceived (not a simple mean of unit prices) so a large batch dominates a
  // small one. Null when there are no receipts yet — UI renders "—".
  const averagePurchasePrice = (() => {
    if (!Array.isArray(history) || history.length === 0) return null;
    let totalCost = 0;
    let totalQty = 0;
    for (const h of history) {
      const qty = Number(h.quantityReceived) || 0;
      const unit = Number(h.unitPurchasePrice) || 0;
      totalCost += qty * unit;
      totalQty += qty;
    }
    return totalQty > 0 ? totalCost / totalQty : null;
  })();

  const fmtDate = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '-');

  const viewMedicineDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={viewMedicineDialogRef} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center"><tc.icon className="h-7 w-7 text-white" /></div>
            <div>
              <h2 className="text-xl font-bold text-white">{medicine.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-white/80 text-sm">{medicine.medicineCode}</span>
                {/* Status pill — solid lighter fill so it pops against the dark header
                    gradient. Tone comes from STATUS_CONFIG (the same green/amber/red
                    palette the row chip uses). */}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm ${sc.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                  {sc.label}
                </span>
                {discontinued && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-800/30 text-white border border-white/40">
                    Discontinued
                  </span>
                )}
              </div>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-3 border-b border-gray-200 flex gap-2 bg-white">
          <button
            onClick={() => setTab('details')}
            className={`px-4 py-2 -mb-px font-medium text-sm border-b-2 ${tab === 'details' ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Eye className="inline w-4 h-4 mr-1.5 align-text-bottom" />
            Details
          </button>
          <button
            onClick={() => setTab('history')}
            className={`px-4 py-2 -mb-px font-medium text-sm border-b-2 ${tab === 'history' ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <History className="inline w-4 h-4 mr-1.5 align-text-bottom" />
            Stock History
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {tab === 'details' ? (
            <div className="grid grid-cols-2 gap-3">
              {[
                // Status moved into the header pill — no row here.
                ['Type', tc.label],
                ['Generic Name', medicine.genericName],
                ['Manufacturer', medicine.manufacturer],
                ['Strength', medicine.strength],
                ['Container Size', medicine.containerSize],
                ['Repackaging', medicine.unitsPerPack > 1 && medicine.packSize && medicine.dispenseSize
                  ? `${medicine.packSize}${medicine.sizeUnit} → ${medicine.dispenseSize}${medicine.sizeUnit} (${medicine.unitsPerPack} units/bottle)`
                  : null],
                ['Low-stock Threshold', medicine.threshold],
                ['Storage Location', medicine.storageLocation],
                // Inventory rollups — summary across all active stock receipts. Per-batch
                // detail lives in the Stock History tab.
                ['Current Quantity', medicine.quantity ?? medicine.totalQuantity],
                ['Current Selling Price', medicine.sellingPrice != null
                  ? `Rs. ${Number(medicine.sellingPrice).toFixed(0)}${medicine.activeBatchCount > 1 ? ' (latest delivery)' : ''}`
                  : null],
                // Quantity-weighted average across every stock receipt on file. Loading
                // state shows "Loading…" so the cell doesn't look empty during the fetch.
                ['Average Purchase Price', loadingHistory
                  ? 'Loading…'
                  : (averagePurchasePrice != null
                    ? `Rs. ${averagePurchasePrice.toFixed(2)}${(history && history.length > 1) ? ` · avg of ${history.length} receipt${history.length === 1 ? '' : 's'}` : ''}`
                    : null)],
                ['Earliest Expiry', fmtDate(medicine.expiryDate ?? medicine.earliestExpiry)],
              ].map(([l, v], i) => (
                <div key={i} className="py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500">{l}</p>
                  <p className="font-medium">{v || '-'}</p>
                </div>
              ))}
            </div>
          ) : (
            <StockHistoryPanel history={history} loading={loadingHistory} fmtDate={fmtDate} />
          )}
        </div>

        {/* Footer — right-aligned emerald Close button matches the visual language of
            the User View modal (full-width grey pill looked unfinished). */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
          <button
            onClick={handleToggleDiscontinued}
            disabled={savingDisc}
            className={`px-4 py-2.5 rounded-xl font-medium text-sm border transition-colors disabled:opacity-50 ${
              discontinued
                ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                : 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100'
            }`}
          >
            {savingDisc ? 'Saving…' : (discontinued ? 'Resume medicine' : 'Mark discontinued')}
          </button>
          <div className="flex items-center gap-3">
            {/* Editing was only reachable from the row's actions menu, so looking at a medicine and
                deciding to change something meant closing this and hunting for it. */}
            {onEdit && (
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                <Edit2 className="h-4 w-4" /> Edit medicine
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockHistoryPanel = ({ history, loading, fmtDate }) => {
  // A medicine bought every week builds up a receipt a week, so the history is paged like any
  // other list.
  const paging = usePagedRows(history || [], { id: 'medicine-stock-history' });
  if (loading) {
    return <div className="text-center text-gray-500 py-8">Loading history…</div>;
  }
  if (!history || history.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="font-medium text-gray-600">No stock receipts yet</p>
        <p className="text-xs">Receipts recorded under Stock Receipts will appear here.</p>
      </div>
    );
  }
  const total = history.reduce((s, h) => s + (h.quantityReceived || 0), 0);
  const totalRemaining = history.reduce((s, h) => s + (h.remaining != null ? h.remaining : (h.quantityReceived || 0)), 0);
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        <span className="font-semibold">{history.length}</span> receipt{history.length === 1 ? '' : 's'} on file · <span className="font-semibold">{total}</span> units received · <span className="font-semibold">{totalRemaining}</span> remaining.
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <TablePager paging={paging} noun="receipt" className="px-3" />
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Invoice #</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Batch</th>
              <th className="px-3 py-2 text-left">Expiry</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-right">Remaining</th>
              <th className="px-3 py-2 text-right">Unit Cost</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paging.pageRows.map((h, i) => (
              <tr
                key={i}
                tabIndex={h.receiptId ? 0 : undefined}
                onClick={() => h.receiptId && openReceipt(h.receiptId)}
                onKeyDown={h.receiptId ? onActivateKey(() => openReceipt(h.receiptId)) : undefined}
                className={h.receiptId ? `cursor-pointer hover:bg-emerald-50/50 ${ROW_FOCUS_RING}` : ''}
                title={h.receiptId ? `Open receipt ${h.invoiceNumber} on the Stock Receipts page` : ''}
              >
                <td className="px-3 py-2 text-gray-700">{fmtDate(h.invoiceDate)}</td>
                <td className="px-3 py-2 font-mono text-xs">{h.invoiceNumber}</td>
                <td className="px-3 py-2">{h.vendorName || '-'}</td>
                <td className="px-3 py-2">{h.batchNumber || '-'}</td>
                <td className="px-3 py-2">{fmtDate(h.expiryDate)}</td>
                <td className="px-3 py-2 text-right">{h.quantityReceived}</td>
                <td className={`px-3 py-2 text-right font-semibold ${h.remaining === 0 ? 'text-gray-400' : ''}`}>
                  {h.remaining != null ? h.remaining : h.quantityReceived}
                </td>
                <td className="px-3 py-2 text-right">Rs. {h.unitPurchasePrice}</td>
                <td className="px-3 py-2 text-right">
                  {h.receiptId && (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="receipt" position="bottom" className="px-3" />
      </div>
    </div>
  );
};

// Jump to the Stock Receipts page with this receipt opened for viewing/editing — same
// sessionStorage handoff the Update Stock modal uses.
const openReceipt = (receiptId) => {
  sessionStorage.setItem('openStockReceiptId', receiptId);
  window.location.hash = '/stock-receipts';
};

// =================== UPDATE STOCK MODAL ===================
// Lists every Stock Receipt that contains the chosen medicine and lets the receptionist
// open one in the Stock Receipts module for editing. If no receipts contain the medicine,
// blocks the action and points the user to add a bill first — per the rule that stock
// updates only happen through receipts.
//
// Navigation strategy: stash the chosen receipt id in sessionStorage under a well-known
// key, then flip the URL hash to /#/stock-receipts. The Stock Receipts page reads the key
// on mount and opens that receipt in its edit modal. Keeps the two modules independent
// without prop-drilling a global state.
const UpdateStockModal = ({ medicine, onClose }) => {
  const [receipts, setReceipts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await stockReceiptAPI.listByMedicine(medicine.id);
        if (!cancelled) setReceipts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load receipts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [medicine.id]);

  const openInStockReceipts = (receiptId) => {
    // Hand off to the Stock Receipts page via sessionStorage. The receiving page consumes
    // and removes the key, then opens the edit modal for the receipt.
    sessionStorage.setItem('openStockReceiptId', receiptId);
    window.location.hash = '/stock-receipts';
    onClose();
  };

  const goToAddStockReceipt = () => {
    window.location.hash = '/stock-receipts';
    onClose();
  };

  const fmt = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '-');

  const updateStockDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={updateStockDialogRef} className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
              <PackagePlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Update Stock</h2>
              <p className="text-xs text-white/85 mt-0.5">
                For <span className="font-semibold">{medicine.name}</span>
                {medicine.medicineCode && <span className="ml-1 font-mono opacity-80">· {medicine.medicineCode}</span>}
              </p>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading && <div className="text-center text-gray-500 py-8">Loading receipts…</div>}
          {!loading && error && <div className="text-center text-red-600 py-8">{error}</div>}

          {!loading && !error && receipts && receipts.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-800 mb-1">No stock receipt found</h3>
              <p className="text-sm text-gray-600 max-w-sm mx-auto mb-4">
                There's no stock receipt on record that contains this medicine. Stock updates
                are tracked through receipts - please add a Bill under{' '}
                <span className="font-semibold">Stock Receipts</span> first.
              </p>
              <button
                onClick={goToAddStockReceipt}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Go to Stock Receipts</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {!loading && !error && receipts && receipts.length > 0 && (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Pick the receipt you want to update - opens the bill on the Stock Receipts page
                where you can edit lines (same-day) or cancel and re-record.
              </p>
              <div className="space-y-2">
                {receipts.map(r => (
                  <button
                    key={r.id}
                    onClick={() => openInStockReceipts(r.id)}
                    className="w-full text-left bg-white border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/40 rounded-xl px-4 py-3 transition-colors flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-mono text-sm font-semibold text-gray-900">{r.invoiceNumber}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          r.status === 'CANCELLED'
                            ? 'bg-gray-200 text-gray-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {r.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {fmt(r.invoiceDate)}
                        {r.vendorName && <span> · {r.vendorName}</span>}
                        {r.lineCount != null && <span> · {r.lineCount} line{r.lineCount === 1 ? '' : 's'}</span>}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-3" />
                  </button>
                ))}
              </div>
              <button
                onClick={goToAddStockReceipt}
                className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Or record a new Stock Receipt</span>
              </button>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50">
          <button onClick={onClose} className="w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Close</button>
        </div>
      </div>
    </div>
  );
};

export default MedicinesManagement;